import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react'
import { demoSteps } from '@/data/frameworks'
import { cn } from '@/lib/utils'

// Import step components
import Step1 from '@/components/steps/Step1'
import Step2 from '@/components/steps/Step2'
import Step3 from '@/components/steps/Step3'
import Step4 from '@/components/steps/Step4'
import Step5 from '@/components/steps/Step5'
import Step6 from '@/components/steps/Step6'

const stepComponents = [Step1, Step2, Step3, Step4, Step5, Step6]

export default function Demo() {
  const [currentStep, setCurrentStep] = useState(0)

  const goToStep = (step: number) => {
    if (step >= 0 && step < demoSteps.length) {
      setCurrentStep(step)
    }
  }

  const nextStep = () => goToStep(currentStep + 1)
  const prevStep = () => goToStep(currentStep - 1)
  const resetDemo = () => setCurrentStep(0)

  const CurrentStepComponent = stepComponents[currentStep]
  const step = demoSteps[currentStep]

  return (
    <div className="min-h-screen pb-20">
      {/* Progress Header */}
      <div className="sticky top-16 z-40 bg-white/90 dark:bg-divine-50/90 backdrop-blur-xl border-b border-gold-200/50 dark:border-gold-800/30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {demoSteps.map((s, index) => (
              <button
                key={s.number}
                onClick={() => goToStep(index)}
                className={cn(
                  'step-indicator',
                  index === currentStep && 'active',
                  index < currentStep && 'completed'
                )}
              >
                {index < currentStep ? '✓' : s.number}
              </button>
            ))}
          </div>

          {/* Progress bar */}
          <div className="sacred-progress">
            <motion.div
              className="sacred-progress-bar"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / demoSteps.length) * 100}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>

          {/* Step title */}
          <div className="mt-4 text-center">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-3"
            >
              <span className="text-3xl">{step.icon}</span>
              <div>
                <h2 className="font-display text-2xl font-bold text-gold-gradient">
                  {step.title}
                </h2>
                <p className="text-sm text-gold-600/70 dark:text-gold-400/70">
                  {step.subtitle}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.4 }}
          >
            <CurrentStepComponent />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-divine-50/90 backdrop-blur-xl border-t border-gold-200/50 dark:border-gold-800/30 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Previous button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={prevStep}
              disabled={currentStep === 0}
              className={cn(
                'flex items-center gap-2 px-6 py-3 rounded-xl font-sans font-medium transition-all',
                currentStep === 0
                  ? 'opacity-50 cursor-not-allowed bg-gold-100 dark:bg-gold-900/20 text-gold-400'
                  : 'bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300 hover:bg-gold-200 dark:hover:bg-gold-800/40'
              )}
            >
              <ChevronLeft className="w-5 h-5" />
              Previous
            </motion.button>

            {/* Step counter & reset */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-gold-600/70 dark:text-gold-400/70">
                Step {currentStep + 1} of {demoSteps.length}
              </span>
              {currentStep > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={resetDemo}
                  className="p-2 rounded-lg bg-gold-100 dark:bg-gold-900/30 text-gold-600 dark:text-gold-400 hover:bg-gold-200 dark:hover:bg-gold-800/40 transition-colors"
                  title="Reset demo"
                >
                  <RotateCcw className="w-4 h-4" />
                </motion.button>
              )}
            </div>

            {/* Next button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={nextStep}
              disabled={currentStep === demoSteps.length - 1}
              className={cn(
                'flex items-center gap-2 px-6 py-3 rounded-xl font-sans font-medium transition-all',
                currentStep === demoSteps.length - 1
                  ? 'opacity-50 cursor-not-allowed bg-gold-400 text-white'
                  : 'btn-golden'
              )}
            >
              {currentStep === demoSteps.length - 1 ? 'Complete' : 'Next'}
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  )
}
